import { IANAZone } from 'luxon';

import {
  TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL,
  TRADE_TYPE_COMMUNITY, TRADE_TYPE_CONTRACTED, TRADE_TYPE_NOMINATED, TRADE_TYPE_RESIDUAL,
} from 'src/util/constants';
import { LOCALE_QUERY_KEY } from 'src/util/i18n/localeUrl';
import { getCalendarDateFromParam } from 'src/util/time';

export const DATE_PARAM = 'date';
export const TIMEZONE_PARAM = 'tz';
export const DIRECTION_PARAM = 'direction';
export const TRADE_TYPE_PARAM = 'tradeType';

// The locale param is owned by the locale URL middleware, which preserves it on
// every navigation, so it is not an unrecognised trade list param.
const KNOWN_PARAMS = [
  DATE_PARAM, TIMEZONE_PARAM, DIRECTION_PARAM, TRADE_TYPE_PARAM, LOCALE_QUERY_KEY,
];

const VALID_DIRECTIONS = [TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL];
const VALID_TYPES = [
  TRADE_TYPE_CONTRACTED, TRADE_TYPE_NOMINATED, TRADE_TYPE_COMMUNITY, TRADE_TYPE_RESIDUAL,
];

/**
 * Parses comma-separated query param values, keeping only known values (deduplicated).
 * @param {Array<string>} params - raw query param values
 * @param {Array<string>} validValues - accepted values
 * @returns {Array<string>} parsed values
 */
const getParamValues = (params, validValues) => {
  const values = new Set(params.flatMap((param) => param.split(',').map((v) => v.trim())));
  return validValues.filter((v) => values.has(v));
};

/**
 * Normalises a query param that may be absent, a string or (when repeated) an array of strings.
 * @param {string|Array<string>|undefined} param - raw query param value
 * @returns {Array<string>} string values
 */
const getStringValues = (param) => [param].flat().filter((v) => typeof v === 'string');

/**
 * Builds the trade list filter from the URL query params.
 *
 * The router gives an array for a repeated param. Repeated `direction` and `tradeType` values
 * are combined (e.g. `tradeType=A&tradeType=B` is the same as `tradeType=A,B`).
 * @param {object} query - URL query object (`match.location.query`)
 * @returns {{direction: Array<string>, type: Array<string>}} filter
 */
export const getFilterFromQuery = (query) => {
  const direction = query?.[DIRECTION_PARAM];
  const type = query?.[TRADE_TYPE_PARAM];
  return {
    direction: getParamValues(getStringValues(direction), VALID_DIRECTIONS),
    type: getParamValues(getStringValues(type), VALID_TYPES),
  };
};

/**
 * Merges the filter into the given query params. Empty filters remove their param.
 * @param {object} params - existing query params
 * @param {{direction: Array<string>, type: Array<string>}} filter - trade list filter
 * @returns {object} new query params
 */
export const buildQueryWithFilter = (params, filter) => {
  const result = { ...params };
  delete result[DIRECTION_PARAM];
  delete result[TRADE_TYPE_PARAM];
  if (filter?.direction?.length > 0) {
    result[DIRECTION_PARAM] = filter.direction.join(',');
  }
  if (filter?.type?.length > 0) {
    result[TRADE_TYPE_PARAM] = filter.type.join(',');
  }
  return result;
};

/**
 * Builds an order-independent key for a list of filter values.
 * @param {Array<string>} values - filter values
 * @returns {string} sorted, comma-joined values
 */
const getSortedKey = (values) => [...values].sort().join(',');

/**
 * Compares two trade list filters by selected values, ignoring order.
 * @param {{direction: Array<string>, type: Array<string>}} a - first filter
 * @param {{direction: Array<string>, type: Array<string>}} b - second filter
 * @returns {boolean} true when both filters select the same values
 */
export const isSameFilter = (a, b) => (
  getSortedKey(a.direction) === getSortedKey(b.direction)
  && getSortedKey(a.type) === getSortedKey(b.type)
);

/**
 * Checks whether any comma-separated value in the params is not an accepted value.
 * @param {Array<string>} params - raw query param values
 * @param {Array<string>} validValues - accepted values
 * @returns {boolean} true when an unknown, non-empty value is present
 */
const hasUnknownValues = (params, validValues) => params
  .flatMap((param) => (typeof param === 'string' ? param.split(',') : []))
  .map((v) => v.trim())
  .some((v) => v !== '' && !validValues.includes(v));

/**
 * Checks whether the URL contains anything the trade list cannot use: an unknown param key, a
 * malformed `date` or `tz`, or an unknown `direction`/`tradeType` value.
 * Empty values and repeated `direction`/`tradeType` params are allowed.
 * @param {object} query - URL query object (`match.location.query`)
 * @returns {boolean} true when the URL contains invalid params
 */
export const hasInvalidQueryParams = (query = {}) => {
  const {
    [DATE_PARAM]: date, [TIMEZONE_PARAM]: tz,
    [DIRECTION_PARAM]: direction, [TRADE_TYPE_PARAM]: type,
  } = query || {};
  const firstTz = Array.isArray(tz) ? tz[0] : tz;
  const firstDate = Array.isArray(date) ? date[0] : date;

  return Object.keys(query || {}).some((key) => !KNOWN_PARAMS.includes(key))
    || (Boolean(firstDate) && getCalendarDateFromParam(firstDate, 'UTC') === null)
    || (Boolean(firstTz) && !IANAZone.isValidZone(firstTz))
    || hasUnknownValues(getStringValues(direction), VALID_DIRECTIONS)
    || hasUnknownValues(getStringValues(type), VALID_TYPES);
};
